import axios from "axios";
import { CONFIG } from "@/config";
import { getToken } from "@/infrastructure/auth/authService";

const AUTH_URL = CONFIG.AUTH_URL;
const ROLES_URL = CONFIG.ROLES_URL;

function authHeaders() {
  return { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" };
}

export interface UserRecord {
  id: string; // maps from userId
  name: string;
  username: string;
  email: string;
  roleName: string;
  status: "ACTIVE" | "INACTIVE";
  createdAt: string;
}

export interface UserListResponse {
  data: UserRecord[];
  total: number;
  page: number;
  limit: number;
}

export interface RoleRecord {
  id: string;       // maps from role_id
  name: string;     // maps from role_name
  description: string; // maps from role_description
  status: string;   // maps from role_status
}

// --- App ID ---
const APP_ID = "NEXO_WAREHOUSE";

// --- Users ---

function normalizeUser(raw: any): UserRecord {
  return {
    id: raw.userId ?? raw.id ?? "",
    name: raw.name ?? "",
    username: raw.username ?? "",
    email: raw.email ?? "",
    roleName: raw.roleName ?? "",
    status: raw.status ?? "ACTIVE",
    createdAt: raw.createdAt ?? raw.createAt ?? "",
  };
}

export async function listUsers(params: { tenantId: string; page?: number; limit?: number }): Promise<UserListResponse> {
  const { tenantId, page = 1, limit = 10 } = params;
  const response = await axios.get<any>(
    `${AUTH_URL}/v1/users?tenantId=${tenantId}&appId=NEXO_WAREHOUSE&page=${page}&limit=${limit}`,
    { headers: authHeaders(), timeout: 10_000 }
  );
  const rawData = response.data.data ?? response.data ?? [];
  const users = Array.isArray(rawData) ? rawData.map(normalizeUser) : [];
  return {
    data: users,
    total: response.data.total ?? users.length,
    page: response.data.page ?? page,
    limit: response.data.limit ?? limit,
  };
}

// --- Roles ---

function normalizeRole(raw: any): RoleRecord {
  return {
    id: raw.role_id ?? raw.id ?? "",
    name: raw.role_name ?? raw.name ?? "",
    description: raw.role_description ?? raw.description ?? "",
    status: raw.role_status ?? raw.status ?? "ACTIVE",
  };
}

export async function listRoles(tenantId: string): Promise<RoleRecord[]> {
  const response = await axios.get<any>(
    `${ROLES_URL}/v1/role/${tenantId}`,
    { headers: authHeaders(), timeout: 10_000 }
  );
  const rawRoles = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
  return rawRoles.map(normalizeRole).filter((r: RoleRecord) => r.name !== "SYSTEM_ADMIN");
}

// --- Mutations ---

export async function createUser(data: { name: string; username: string; email: string; password: string; tenantId: string; roleName: string }): Promise<void> {
  await axios.post(
    `${AUTH_URL}/v1/users`,
    { ...data, appId: "NEXO_WAREHOUSE" },
    { headers: authHeaders(), timeout: 10_000 }
  );
}

export async function updateUser(userId: string, data: { name?: string; username?: string; email?: string; password?: string }): Promise<void> {
  await axios.patch(
    `${AUTH_URL}/v1/users/${userId}`,
    data,
    { headers: authHeaders(), timeout: 10_000 }
  );
}

export async function updateUserRole(userId: string, tenantId: string, roleId: string): Promise<void> {
  await axios.put(
    `${ROLES_URL}/v1/memberships/${userId}/tenant/${tenantId}/app/${APP_ID}/role`,
    { roleId },
    { headers: authHeaders(), timeout: 10_000 }
  );
}

export async function updateUserStatus(userId: string, status: "ACTIVE" | "INACTIVE"): Promise<void> {
  await axios.patch(
    `${AUTH_URL}/v1/users/${userId}/status`,
    { status },
    { headers: authHeaders(), timeout: 10_000 }
  );
}
